import React, { useState, useEffect } from 'react';
import { portalOrdersAPI } from '../services/portalAPI';

interface TrackingEvent {
  event_date: string;
  event_time: string | null;
  activity: string;
  location: string;
}

interface TrackingData {
  tracking_available: boolean;
  message?: string;
  carrier?: string;
  pro_number?: string;
  peninsula_pro?: string;
  pro_display?: string;
  ship_date?: string;
  status?: string;
  is_delivered?: boolean;
  delivery_date?: string;
  origin_terminal?: string;
  destination_terminal?: string;
  events?: TrackingEvent[];
  tracking_url?: string;
  last_updated?: string;
}

interface ShipmentTrackingProps {
  orderId: number;
  orderStatus: string;
}

const ShipmentTracking: React.FC<ShipmentTrackingProps> = ({ orderId, orderStatus }) => {
  const [tracking, setTracking] = useState<TrackingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Only fetch tracking for shipped or delivered orders
    if (orderStatus === 'SHIPPED' || orderStatus === 'DELIVERED') {
      fetchTracking();
    } else {
      setLoading(false);
    }
  }, [orderId, orderStatus]);

  const fetchTracking = async () => {
    try {
      setLoading(true);
      const response = await portalOrdersAPI.getTracking(orderId);
      setTracking(response.data);
    } catch (err: any) {
      setError('Unable to load tracking information');
      console.error('Tracking error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateStr: string, timeStr: string | null): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const dateFormatted = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    return timeStr ? `${dateFormatted} at ${timeStr}` : dateFormatted;
  };

  // Don't show anything for non-shipped orders
  if (orderStatus !== 'SHIPPED' && orderStatus !== 'DELIVERED') {
    return null;
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingSpinner}>Loading tracking info...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorMessage}>{error}</div>
      </div>
    );
  }

  if (!tracking?.tracking_available) {
    return (
      <div style={styles.container}>
        <div style={styles.noTracking}>
          <span style={styles.noTrackingIcon}>📦</span>
          <span>Tracking information will be available once your order ships via freight carrier.</span>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.headerIcon}>🚚</span>
          <div style={styles.headerText}>
            <h3 style={styles.headerTitle}>Shipment Tracking</h3>
            <div style={styles.proDisplay}>{tracking.pro_display || `PRO: ${tracking.pro_number}`}</div>
          </div>
        </div>
        <div style={{
          ...styles.statusBadge,
          ...(tracking.is_delivered ? styles.statusDelivered : styles.statusInTransit)
        }}>
          {tracking.is_delivered ? '✓ Delivered' : '🚛 In Transit'}
        </div>
      </div>

      {/* Status */}
      <div style={styles.statusSection}>
        <div style={styles.statusText}>{tracking.status}</div>
        {tracking.origin_terminal && tracking.destination_terminal && (
          <div style={styles.terminals}>
            <span>{tracking.origin_terminal}</span>
            <span style={styles.arrow}>→</span>
            <span>{tracking.destination_terminal}</span>
          </div>
        )}
      </div>

      {/* Tracking Events Timeline */}
      {tracking.events && tracking.events.length > 0 && (
        <div style={styles.eventsSection}>
          <h4 style={styles.eventsTitle}>Tracking History</h4>
          <div style={styles.timeline}>
            {tracking.events.map((event, index) => (
              <div key={index} style={styles.timelineItem}>
                <div style={styles.timelineDotContainer}>
                  <div style={{
                    ...styles.timelineDot,
                    ...(index === 0 ? (tracking.is_delivered ? styles.dotDelivered : styles.dotCurrent) : styles.dotPast)
                  }} />
                  {tracking.events && index < tracking.events.length - 1 && (
                    <div style={styles.timelineLine} />
                  )}
                </div>
                <div style={styles.timelineContent}>
                  <div style={styles.eventActivity}>{event.activity}</div>
                  <div style={styles.eventMeta}>
                    {formatDateTime(event.event_date, event.event_time)}
                    {event.location && ` • ${event.location}`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer with link and timestamp */}
      <div style={styles.footer}>
        {tracking.tracking_url && (
          <a
            href={tracking.tracking_url}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.trackingLink}
          >
            View on Carrier Website →
          </a>
        )}
        {tracking.last_updated && (
          <div style={styles.lastUpdated}>
            Last updated: {new Date(tracking.last_updated).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    backgroundColor: '#f0fdf4',
    border: '1px solid #86efac',
    borderRadius: '8px',
    padding: '1.25rem',
    marginBottom: '1.5rem',
  },
  loadingSpinner: {
    textAlign: 'center',
    color: '#6b7280',
    padding: '1rem',
  },
  errorMessage: {
    textAlign: 'center',
    color: '#dc2626',
    padding: '1rem',
  },
  noTracking: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    color: '#6b7280',
    fontStyle: 'italic',
  },
  noTrackingIcon: {
    fontSize: '1.5rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1rem',
    flexWrap: 'wrap',
    gap: '0.75rem',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  headerIcon: {
    fontSize: '1.75rem',
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
  },
  headerTitle: {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#166534',
    margin: 0,
  },
  proDisplay: {
    fontSize: '0.875rem',
    color: '#4b5563',
    marginTop: '0.25rem',
  },
  statusBadge: {
    padding: '0.375rem 0.75rem',
    borderRadius: '20px',
    fontSize: '0.875rem',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
  },
  statusDelivered: {
    backgroundColor: '#dcfce7',
    color: '#166534',
  },
  statusInTransit: {
    backgroundColor: '#dbeafe',
    color: '#1e40af',
  },
  statusSection: {
    marginBottom: '1rem',
    paddingBottom: '1rem',
    borderBottom: '1px solid #bbf7d0',
  },
  statusText: {
    fontSize: '0.95rem',
    color: '#374151',
    marginBottom: '0.5rem',
  },
  terminals: {
    fontSize: '0.875rem',
    color: '#6b7280',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  arrow: {
    color: '#9ca3af',
  },
  eventsSection: {
    marginBottom: '1rem',
  },
  eventsTitle: {
    fontSize: '0.95rem',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '0.75rem',
    margin: 0,
  },
  timeline: {
    display: 'flex',
    flexDirection: 'column',
  },
  timelineItem: {
    display: 'flex',
    gap: '0.75rem',
  },
  timelineDotContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '16px',
  },
  timelineDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  dotDelivered: {
    backgroundColor: '#22c55e',
    boxShadow: '0 0 0 3px rgba(34, 197, 94, 0.2)',
  },
  dotCurrent: {
    backgroundColor: '#3b82f6',
    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.2)',
  },
  dotPast: {
    backgroundColor: '#d1d5db',
  },
  timelineLine: {
    width: '2px',
    flexGrow: 1,
    backgroundColor: '#d1d5db',
    marginTop: '4px',
    marginBottom: '4px',
    minHeight: '24px',
  },
  timelineContent: {
    paddingBottom: '1rem',
    flex: 1,
  },
  eventActivity: {
    fontSize: '0.95rem',
    fontWeight: '500',
    color: '#1f2937',
  },
  eventMeta: {
    fontSize: '0.8rem',
    color: '#6b7280',
    marginTop: '0.125rem',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0.5rem',
    borderTop: '1px solid #bbf7d0',
    paddingTop: '0.75rem',
  },
  trackingLink: {
    color: '#166534',
    fontWeight: '500',
    fontSize: '0.875rem',
    textDecoration: 'none',
  },
  lastUpdated: {
    fontSize: '0.75rem',
    color: '#9ca3af',
  },
};

export default ShipmentTracking;
